import { useEffect, useState } from "react";
import {
  AttorneysStatusMapped,
  errorInstructions,
  LeadType,
  Milestone,
} from "../shared/Constants";
import {
  AppBreadcrumbs,
  AppButton,
  EmptyState,
  FormSection,
  PageHeader,
  StageStatusChip,
  StatusChip,
  toast,
} from "../../../../components/ui";
import EmploymentForm from "./EmploymentForm";

import Swal from "sweetalert2";
import withReactContent from "sweetalert2-react-content";
import { LeadsService } from "../../../../api/services/LeadsService";
import {
  AdminLeadStatus,
  ApplicationRole,
  AttorneyApprovalStatus,
  AttorneyLeadStatus,
  AttorneyStatus,
  Category,
  LeadViewModel,
  OpenAPI,
  ReferralCaseProgressViewModel,
} from "../../../../api";
import { HelpersService } from "../../../../services/Helpers";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import LeadForm from "./LeadForm";
import LeadMenu from "./LeadMenu/LeadMenu";
import MilestoneBar from "./LeadMenu/MilestoneBar";
import { useDispatch, useSelector } from "react-redux";
import { setAttorneysData } from "../../../../redux/actions/attorneyReferActions";
import {
  startSpinner,
  stopSpinner,
} from "../../../../redux/actions/spinnerActions";
import { Box, Stack, Tooltip } from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import HistoryIcon from "@mui/icons-material/History";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import DownloadOutlinedIcon from "@mui/icons-material/DownloadOutlined";
import TransformOutlinedIcon from "@mui/icons-material/TransformOutlined";
import EmojiEventsOutlinedIcon from "@mui/icons-material/EmojiEventsOutlined";
import AttorneysRow from "./AttorneysTableBlock";
import ChangeHistoryDrawer from "./ChangeHistoryTab";
import { LEAD_SIDE_RAIL_ID } from "./LeadMenu/LeadSideRailPortal";
import CaseStatusSummary from "./CaseStatusSummary";
import {
  getAttorneyStageLabel,
  getReferralPipelineLabel,
} from "../shared/attorneyStageLabels";
import "../Leads.scss";

const MySwal = withReactContent(Swal);

let milestones: Milestone[] = [];

function leadListCrumb(
  status?: AdminLeadStatus | null,
  leadType?: LeadType
): { label: string; to: string } {
  if (leadType === LeadType.Attorney || leadType === LeadType.Case) {
    return { label: "Cases", to: "/attorney-cases" };
  }
  if (leadType === LeadType.StaffAttorney) {
    return { label: "My leads", to: "/attorney-leads" };
  }

  switch (status) {
    case AdminLeadStatus.NEW:
    case AdminLeadStatus.DRAFT:
      return { label: "New", to: "/new-leads" };
    case AdminLeadStatus.DONE:
    case AdminLeadStatus.SUPPLEMENTAL_ASSIGNED:
    case AdminLeadStatus.CONTRACT_SENT:
    case AdminLeadStatus.CONTRACT_SIGNED:
    case AdminLeadStatus.PAID:
      return { label: "Active clients", to: "/active-clients" };
    case AdminLeadStatus.N_A:
    case AdminLeadStatus.NGC:
    case AdminLeadStatus.ATTORNEY_REJECT:
    case AdminLeadStatus.CLIENT_RETAINED:
    case AdminLeadStatus.DECLINED:
    case AdminLeadStatus.CSADECLINED:
      return { label: "Archived", to: "/archive" };
    case AdminLeadStatus.SHARED:
    case AdminLeadStatus.REJECTED:
    case AdminLeadStatus.WINNER_SELECTED:
    case AdminLeadStatus.INTRODUCTION:
    case AdminLeadStatus.SENT_FOR_SIGNING:
    case AdminLeadStatus.SIGNED:
    case AdminLeadStatus.CLIENT_REFERED:
      return { label: "Referrals", to: "/referral-leads" };
    default:
      return { label: "All", to: "/all" };
  }
}

export default function LeadDetailsView() {
  const [details, setDetails] = useState<LeadViewModel | null>(null);
  const [isEdit, setIsEdit] = useState<boolean>(false);
  const [leadType, setLeadType] = useState<LeadType>(LeadType.Admin);
  const [historyOpen, setHistoryOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  const [caseProgress, setCaseProgress] = useState<ReferralCaseProgressViewModel | null>();
  const { id } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const user = useSelector((state: any) => state.user?.user);
  const attorneysData = useSelector((state: any) => state.attorneys?.attorneys);
  const dispatch = useDispatch();
  const isAdmin = user?.roles?.includes(ApplicationRole.ADMIN);

  useEffect(() => {
    if (!id || !user) {
      return;
    }
    const queryParams = new URLSearchParams(location.search);

    var nextLeadType = queryParams.get("leadType");
    const edit = queryParams.get("isEdit") ?? "false";
    if (!nextLeadType) {
      const rolesSet = new Set(user.roles);

      switch (true) {
        case rolesSet.has(ApplicationRole.ADMIN):
        case rolesSet.has(ApplicationRole.QA):
        case rolesSet.has(ApplicationRole.STAFF): {
          nextLeadType = LeadType.Admin;
          break;
        }
        case rolesSet.has(ApplicationRole.STAFF_ATTORNEY): {
          nextLeadType = LeadType.StaffAttorney;
          break;
        }
        default: {
          nextLeadType = LeadType.Attorney;
          break;
        }
      }
    }
    handleOpenDetails(
      +id,
      HelpersService.getEnumKeyByEnumValue(LeadType, nextLeadType),
      JSON.parse(edit)
    );
  }, [id, user]);

  const canUpdateCaseProgress =
    details?.attorneyLeadStatus === AttorneyLeadStatus.PAID ||
    (details?.attorneyLeadStatus === AttorneyLeadStatus.SIGNED &&
      details?.withoutPayment === true);

  const showMainCaseStatus =
    (leadType === LeadType.Case || leadType === LeadType.Attorney) &&
    canUpdateCaseProgress;

  useEffect(() => {
    if (!details?.id || !showMainCaseStatus) {
      setCaseProgress(null);
      return;
    }
    LeadsService.getApiLeadsReferralCaseProgress(details.id)
      .then((data) => setCaseProgress(data ?? null))
      .catch(() => setCaseProgress(null));
  }, [details?.id, showMainCaseStatus]);

  useEffect(() => {
    if (!details?.id) {
      return;
    }

    const syncStickyOffset = () => {
      const detailsElement = document.getElementById("lead-details");
      if (!detailsElement) {
        return;
      }

      detailsElement.style.marginTop = "";

      const topBlock = detailsElement.querySelector(
        ".lead-details-top"
      ) as HTMLElement | null;
      const sideOffset = (topBlock?.offsetHeight ?? 0) + 12;
      document.documentElement.style.setProperty(
        "--lead-side-offset",
        `${Math.max(sideOffset, 120)}px`
      );
    };

    syncStickyOffset();
    const frame = window.requestAnimationFrame(syncStickyOffset);
    window.addEventListener("resize", syncStickyOffset);
    return () => {
      window.cancelAnimationFrame(frame);
      window.removeEventListener("resize", syncStickyOffset);
      document.documentElement.style.removeProperty("--lead-side-offset");
    };
  }, [
    id,
    user,
    details?.status,
    details?.attorneyApprovalStatus,
    leadType,
    isEdit,
    details?.id,
  ]);

  const handleEditClick = () => {
    setIsEdit(!isEdit);
    if (isEdit) {
      EmploymentForm.resetFormWith();
    }
  };

  const downloadPdf = () => {
    if (!details?.id) {
      return;
    }

    fetch(`${OpenAPI.BASE}/api/Leads/${details.id}/pdfIntake`, {
      method: "GET",
      headers: {
        Authorization: (OpenAPI.HEADERS as any)?.Authorization,
      },
    }).then((response: any) => {
      response.blob().then((blob) => {
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.style.display = "none";
        a.href = url;
        if (details.attorneyApprovalStatus === AttorneyApprovalStatus.REFER) {
          a.download = `Intake notes.pdf`;
        } else {
          a.download = `Intake notes for ${details.firstName} ${details.lastName}.pdf`;
        }
        document.body.appendChild(a);
        a.click();
      });
    });

    if (details.status === AdminLeadStatus.DONE) {
      fetch(`${OpenAPI.BASE}/api/Leads/${details.id}/pdfSupplemental`, {
        method: "GET",
        headers: {
          Authorization: (OpenAPI.HEADERS as any)?.Authorization,
        },
      }).then((response: any) => {
        response.blob().then((blob) => {
          const url = window.URL.createObjectURL(blob);
          const a = document.createElement("a");
          a.style.display = "none";
          a.href = url;
          a.download = `Supplemental notes for ${details.firstName} ${details.lastName}.pdf`;
          document.body.appendChild(a);
          a.click();
        });
      });
    }
  };

  const convertClassForm = () => {
    if (!details?.id) {
      return;
    }
    dispatch(startSpinner());
    LeadsService.putApiLeadsConvertToClass(details.id)
      .then((url) => {
        navigate(`${url}?isEdit=true`);
      })
      .catch(() =>
        toast.error(errorInstructions || "Error converting to Class action.")
      )
      .finally(() => dispatch(stopSpinner()));
  };

  const handleOpenDetails = (
    leadId: number,
    nextLeadType: LeadType,
    editMode
  ) => {
    setLoading(true);
    setDetails(null);
    setLeadType(nextLeadType);
    dispatch(startSpinner());
    LeadsService.getApiLeads(leadId)
      .then((lead: LeadViewModel) => {
        lead = HelpersService.parseLeadResponse(lead);
        if (
          lead.status === AdminLeadStatus.SHARED ||
          lead.status === AdminLeadStatus.REJECTED
        ) {
          if (
            user.roles?.includes(ApplicationRole.ADMIN) ||
            user.roles?.includes(ApplicationRole.REFFERAL_STAFF)
          ) {
            LeadsService.getApiLeadsAttorneys(leadId).then((attorneys) => {
              var mappedAttorneys = Object.values(AttorneyStatus).map(
                (status) => {
                  return {
                    status: status,
                    attorneys: attorneys.filter(
                      (attorney) => attorney.status! === status
                    ),
                  } as AttorneysStatusMapped;
                }
              );
              dispatch(
                setAttorneysData({
                  sharedAttorneys: attorneys,
                  sharedAttorneysStatusMapped: mappedAttorneys,
                })
              );
            });
          }
          if (
            ((user.roles?.includes(ApplicationRole.ADMIN) ||
              user.roles?.includes(ApplicationRole.REFFERAL_STAFF)) &&
              (lead.status === AdminLeadStatus.SHARED ||
                lead.status === AdminLeadStatus.REJECTED)) ||
            user.roles?.includes(ApplicationRole.ATTORNEY)
          ) {
            LeadsService.postApiLeadsSetOpen(leadId).then();
          }
        }

        setDetails(lead);
        setIsEdit(editMode);
        if (
          lead.attorneyApprovalStatus === AttorneyApprovalStatus.IN_PROCESS ||
          lead.attorneyApprovalStatus ===
            AttorneyApprovalStatus.MORE_INFO_REQUESTED ||
          lead.attorneyApprovalStatus ===
            AttorneyApprovalStatus.MORE_INFO_PROVIDED ||
          lead.status === AdminLeadStatus.DRAFT ||
          lead.status === AdminLeadStatus.DECLINED ||
          lead.status === AdminLeadStatus.NGC ||
          lead.status === AdminLeadStatus.N_A ||
          lead.status === AdminLeadStatus.CSADECLINED ||
          lead.status === AdminLeadStatus.CLIENT_RETAINED ||
          lead.status === AdminLeadStatus.CLIENT_REFERED
        ) {
          milestones = [];
          return;
        }

        switch (lead.status) {
          case AdminLeadStatus.NEW: {
            milestones =
              lead.attorneyApprovalStatus === AttorneyApprovalStatus.REFER
                ? [
                    { name: "New", done: true },
                    { name: "Shared", done: false },
                    { name: "Winner selected", done: false },
                    { name: "Sent for signing", done: false },
                    { name: "Agreement signed", done: false },
                    { name: "Paid", done: false },
                  ]
                : [
                    { name: "New", done: true },
                    { name: "Contract sent", done: false },
                    { name: "Contract signed", done: false },
                    { name: "Supplemental assigned", done: false },
                    { name: "Supplemental filled", done: false },
                  ];
            break;
          }
          case AdminLeadStatus.REJECTED:
          case AdminLeadStatus.SHARED: {
            milestones = [
              { name: "New", done: true },
              { name: "Shared", done: true },
              { name: "Winner selected", done: false },
              { name: "Sent for signing", done: false },
              { name: "Agreement signed", done: false },
              { name: "Paid", done: false },
            ];
            break;
          }
          case AdminLeadStatus.INTRODUCTION: {
            milestones =
              nextLeadType === LeadType.Attorney
                ? [
                    { name: "Accept", done: true },
                    { name: "Awaiting GLio confirmation", done: true },
                    { name: "Opportunity won", done: false },
                    {
                      name: (lead.adminFee ?? 0) > 0 ? "Pay & Sign" : "Sign",
                      done: false,
                    },
                  ]
                : [
                    { name: "New", done: true },
                    { name: "Shared", done: true },
                    { name: "Winner selected", done: true },
                    { name: "Sent for signing", done: false },
                    { name: "Agreement signed", done: false },
                    { name: "Paid", done: false },
                  ];
            break;
          }
          case AdminLeadStatus.WINNER_SELECTED: {
            milestones = [
              { name: "New", done: true },
              { name: "Shared", done: true },
              { name: "Winner selected", done: true },
              { name: "Sent for signing", done: false },
              { name: "Agreement signed", done: false },
              { name: "Paid", done: false },
            ];
            break;
          }
          case AdminLeadStatus.SENT_FOR_SIGNING: {
            milestones = [
              { name: "New", done: true },
              { name: "Shared", done: true },
              { name: "Winner selected", done: true },
              { name: "Sent for signing", done: true },
              { name: "Agreement signed", done: false },
              { name: "Paid", done: false },
            ];
            break;
          }
          case AdminLeadStatus.SIGNED: {
            milestones = [
              { name: "New", done: true },
              { name: "Shared", done: true },
              { name: "Winner selected", done: true },
              { name: "Sent for signing", done: true },
              { name: "Agreement signed", done: true },
              { name: "Paid", done: false },
            ];
            break;
          }
          case AdminLeadStatus.PAID: {
            milestones = [
              { name: "New", done: true },
              { name: "Shared", done: true },
              { name: "Winner selected", done: true },
              { name: "Sent for signing", done: true },
              { name: "Agreement signed", done: true },
              { name: "Paid", done: true },
            ];
            break;
          }
          case AdminLeadStatus.CONTRACT_SENT: {
            milestones = [
              { name: "New", done: true },
              { name: "Contract sent", done: true },
              { name: "Contract signed", done: false },
              { name: "Supplemental assigned", done: false },
              { name: "Supplemental filled", done: false },
            ];
            break;
          }
          case AdminLeadStatus.CONTRACT_SIGNED: {
            milestones = [
              { name: "New", done: true },
              { name: "Contract sent", done: true },
              { name: "Contract signed", done: true },
              { name: "Supplemental assigned", done: false },
              { name: "Supplemental filled", done: false },
            ];
            break;
          }
          case AdminLeadStatus.SUPPLEMENTAL_ASSIGNED: {
            milestones = [
              { name: "New", done: true },
              { name: "Contract sent", done: true },
              { name: "Contract signed", done: true },
              { name: "Supplemental assigned", done: true },
              { name: "Supplemental filled", done: false },
            ];
            break;
          }
          case AdminLeadStatus.DONE: {
            milestones = [
              { name: "New", done: true },
              { name: "Contract sent", done: true },
              { name: "Contract signed", done: true },
              { name: "Supplemental assigned", done: true },
              { name: "Supplemental filled", done: true },
            ];
            break;
          }
        }

        switch (lead.attorneyLeadStatus) {
          case AttorneyLeadStatus.NEW: {
            milestones = [
              { name: "Accept/Reject", done: true },
              { name: "Awaiting GLio confirmation", done: false },
              { name: "Opportunity won", done: false },
              {
                name: (lead.adminFee ?? 0) > 0 ? "Pay & Sign" : "Sign",
                done: false,
              },
            ];
            break;
          }
          case AttorneyLeadStatus.ACCEPTED: {
            milestones = [
              { name: "Accept", done: true },
              { name: "Awaiting GLio confirmation", done: true },
              { name: "Opportunity won", done: false },
              {
                name: (lead.adminFee ?? 0) > 0 ? "Pay & Sign" : "Sign",
                done: false,
              },
            ];
            break;
          }
          case AttorneyLeadStatus.REJECTED: {
            milestones = [{ name: "Reject", done: true }];
            break;
          }
          case AttorneyLeadStatus.LOST: {
            milestones = [
              { name: "Accept", done: true },
              { name: "Awaiting GLio confirmation", done: true },
              { name: "Opportunity lost", done: true },
            ];
            break;
          }
          case AttorneyLeadStatus.WON: {
            milestones = [
              { name: "Accept", done: true },
              { name: "Awaiting GLio confirmation", done: true },
              { name: "Opportunity won", done: true },
              {
                name: (lead.adminFee ?? 0) > 0 ? "Pay & Sign" : "Sign",
                done: !!lead.isPaid,
              },
            ];
            break;
          }
        }

        if (lead.withoutPayment === true) {
          milestones = milestones.filter((m) => m.name !== "Paid");
        }
      })
      .catch(() => {
        setDetails(null);
        MySwal.fire({
          icon: "error",
          title: <p className="title">Error retrieving lead data</p>,
        });
      })
      .finally(() => {
        setLoading(false);
        dispatch(stopSpinner());
      });
  };

  if (loading || !details?.id) {
    return null;
  }

  const listCrumb = leadListCrumb(details.status, leadType);
  const clientName =
    [details.firstName, details.lastName].filter(Boolean).join(" ").trim() ||
    "Lead details";
  const showWinnerPanel =
    (details.status === AdminLeadStatus.SHARED ||
      details.status === AdminLeadStatus.REJECTED) &&
    (user.roles?.includes(ApplicationRole.ADMIN) ||
      user.roles?.includes(ApplicationRole.REFFERAL_STAFF));
  const winnerRows = attorneysData?.sharedAttorneysStatusMapped ?? [];
  const canManageLead =
    leadType === LeadType.Admin &&
    details.status !== AdminLeadStatus.ATTORNEY_REJECT;
  const canEditLead =
    canManageLead &&
    details.status !== AdminLeadStatus.DRAFT &&
    (user.roles?.includes(ApplicationRole.ADMIN) ||
      user.roles?.includes(ApplicationRole.STAFF));
  const canConvertToClass =
    canEditLead && details.category === Category.EMPLOYMENT;
  const categoryLabel = details.category
    ? HelpersService.camelToString(details.category, false)
    : "";
  const headerActions = (
    <>
      <Tooltip title={`Back to ${listCrumb.label}`}>
        <AppButton
          variant="outlined"
          color="inherit"
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate(listCrumb.to)}
        >
          Back
        </AppButton>
      </Tooltip>
      {(canManageLead || isAdmin) && (
        <>
          {canEditLead && (
            <AppButton
              variant="contained"
              color={isEdit ? "primary" : "secondary"}
              startIcon={<EditOutlinedIcon />}
              onClick={handleEditClick}
            >
              {isEdit ? "Cancel edit" : "Edit lead"}
            </AppButton>
          )}
          {canManageLead && (
            <AppButton
              variant="outlined"
              color="primary"
              startIcon={<DownloadOutlinedIcon />}
              onClick={downloadPdf}
            >
              Download PDF
            </AppButton>
          )}
          {canConvertToClass && (
            <AppButton
              variant="outlined"
              color="inherit"
              startIcon={<TransformOutlinedIcon />}
              onClick={convertClassForm}
            >
              Convert to Class Action
            </AppButton>
          )}
          {isAdmin && (
            <AppButton
              variant="outlined"
              color="primary"
              startIcon={<HistoryIcon />}
              onClick={() => setHistoryOpen(true)}
            >
              Show history
            </AppButton>
          )}
        </>
      )}
    </>
  );

  const showMilestones =
    leadType !== LeadType.StaffAttorney && milestones.length > 0;

  return (
    <>
      <div
        className="lead-details-holder lead-details-holder--flush"
        id="lead-details"
      >
        <Box className="lead-details-page">
          <Box className="lead-details-top">
            <AppBreadcrumbs
              className="lead-details-breadcrumbs"
              items={[
                {
                  label: listCrumb.label,
                  to: listCrumb.to,
                },
                {
                  label: clientName,
                },
              ]}
            />
            <PageHeader
              className="lead-details-header"
              title={`${categoryLabel} lead`.trim()}
              subtitle={
                <Stack
                  direction="row"
                  alignItems="center"
                  spacing={1}
                  flexWrap="wrap"
                  useFlexGap
                  sx={{ mt: 0.25 }}
                >
                  <span>{clientName}</span>
                  {details.attorneyApprovalStatus === AttorneyApprovalStatus.REFER &&
                    details.attorneyLeadStatus && (
                      <StageStatusChip
                        status={details.attorneyLeadStatus}
                        label={getAttorneyStageLabel(
                          details.attorneyLeadStatus,
                          details.withoutPayment
                        )}
                        size="small"
                      />
                    )}
                  {!!details.status &&
                    details.attorneyApprovalStatus === AttorneyApprovalStatus.REFER && (
                      <StageStatusChip
                        status={details.status}
                        label={getReferralPipelineLabel(details.status)}
                        size="small"
                        sx={{ opacity: 0.9 }}
                      />
                    )}
                  {!!details.status &&
                    details.attorneyApprovalStatus !== AttorneyApprovalStatus.REFER && (
                      <StageStatusChip status={details.status} size="small" />
                    )}
                  {!!details.approvalType && (
                    <StatusChip
                      tone={details.approvalType === "High" ? "dirtyGreen" : "warning"}
                      label={`${details.approvalType} quality`}
                      size="small"
                    />
                  )}
                </Stack>
              }
              actions={headerActions}
            />
            {showMilestones && <MilestoneBar milestones={milestones} />}
          </Box>

          <Box className="lead-details-body lead-details-body--split">
            <Box className="lead-details-main">
              {showMainCaseStatus && (
                <CaseStatusSummary
                  className="lead-details-case-status mb-4"
                  progress={caseProgress}
                  leadId={details.id}
                  canEdit
                  onEdit={() => navigate(`/lead/${details.id}/referral-case`)}
                />
              )}
              <LeadForm
                details={details}
                isEdit={isEdit}
                leadType={leadType}
              />
            </Box>
            <Box id={LEAD_SIDE_RAIL_ID} className="lead-details-side">
              {showWinnerPanel && (
                <FormSection
                  className="lead-side-panel"
                  title="Pick a winner"
                  subtitle="Review responses and select an accepted attorney."
                  icon={<EmojiEventsOutlinedIcon />}
                >
                  {winnerRows.length === 0 ? (
                    <EmptyState
                      compact
                      title="No attorneys yet"
                      description="Shared attorneys will appear here once available."
                    />
                  ) : (
                    <Stack className="winner-panel" spacing={1.25}>
                      {winnerRows.map((row, index) => (
                        <AttorneysRow
                          leadId={details.id!}
                          key={`${row.status}-${index}`}
                          row={row}
                          defaultExpanded={
                            row.status === AttorneyStatus.ACCEPTED &&
                            (row.attorneys?.length ?? 0) > 0
                          }
                        />
                      ))}
                    </Stack>
                  )}
                </FormSection>
              )}
            </Box>
          </Box>
        </Box>
      </div>
      {/* Render after the side rail exists so portals can attach. */}
      <LeadMenu
        leadType={leadType}
        userRoles={user.roles}
        details={details}
      />
      {isAdmin && (
        <ChangeHistoryDrawer
          leadId={details.id ?? (id ? +id : null)}
          open={historyOpen}
          onClose={() => setHistoryOpen(false)}
        />
      )}
    </>
  );
}
